import React from 'react';
import styled from 'styled-components';
import { PrimaryButton, TertiaryButton } from 'styleguide/Buttons';
import { colors } from 'styleguide/colors';
import { DividerLine } from 'styleguide/DividerLine';
import { LabelledFormInput } from 'styleguide/Inputs';
import { H1, P2, P3 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { LoginWithGoogleButton, SocialLoginButton } from '../../../components/SocialLoginButton';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const Spacer = styled.div`
  margin-top: 92px;
  @media (max-width: 1024px) {
    margin-top: 16px;
  }
`;

interface LoginCardProps {
  usernameOrEmail: string;
  isEmailOrUsernameLocked?: boolean;
  onUpdateUsernameOrEmail: (value: string) => void;
  password: string;
  onUpdatePassword: (value: string) => void;
  isLoginEnabled: boolean;
  isLoading: boolean;
  onLogin: () => void;
  loginErrorMessage?: string;
  onGoogleLogin: () => void;
  onAppleLogin: () => void;
  onForgotPassword: () => void;
  onNewUserPress: () => void;
}

export const LoginCard = ({
  usernameOrEmail,
  isEmailOrUsernameLocked,
  onUpdateUsernameOrEmail,
  password,
  onUpdatePassword,
  isLoginEnabled,
  isLoading,
  onLogin,
  loginErrorMessage,
  onGoogleLogin,
  onAppleLogin,
  onForgotPassword,
  onNewUserPress,
}: LoginCardProps) => {
  return (
    <View style={{ width: '100%', maxWidth: 400 }}>
      <View style={{ flex: 1 }}>
        <Spacer />
        <H1 style={{ textAlign: 'center', marginBottom: 48 }}>{t('web.login.loginCard.title')}</H1>
        <LoginWithGoogleButton
          action="login"
          style={{ marginBottom: 16, justifyContent: 'center' }}
          onClick={onGoogleLogin}
        />
        <SocialLoginButton
          action="login"
          style={{ marginBottom: 16 }}
          type="apple"
          onClick={onAppleLogin}
        />
        <FlexRow style={{ marginTop: 16, marginBottom: 20 }}>
          <DividerLine />
          <P3 type="secondary" style={{ width: '100%', textAlign: 'center' }}>
            {t('web.login.loginCard.orWithEmail')}
          </P3>
          <DividerLine />
        </FlexRow>
        <form onSubmit={e => e.preventDefault()}>
          <LabelledFormInput
            disabled={isEmailOrUsernameLocked ?? false}
            label={t('web.login.loginCard.formInput.emailUsername')}
            spellCheck={false}
            containerStyle={{ marginBottom: '16px' }}
            value={usernameOrEmail}
            autoCapitalize="none"
            onChange={event => onUpdateUsernameOrEmail(event.currentTarget.value)}
          />
          <LabelledFormInput
            label={t('web.login.loginCard.formInput.password')}
            type={'password'}
            containerStyle={{ marginBottom: '16px' }}
            value={password}
            onChange={event => onUpdatePassword(event.currentTarget.value)}
          />
          <PrimaryButton
            type={'submit'}
            title={t('web.login.loginCard.formInput.loginBtn')}
            size="lg"
            style={{ width: '100%', marginBottom: '24px' }}
            enabled={isLoginEnabled}
            loading={isLoading}
            onClick={onLogin}
          />
        </form>
        {!!loginErrorMessage && (
          <View>
            <P2 style={{ color: colors.red, marginBottom: '16px' }}>{loginErrorMessage}</P2>
          </View>
        )}
        <TertiaryButton
          onClick={onForgotPassword}
          style={{ width: '100%', marginBottom: 0, textDecoration: 'underline' }}
        >
          {t('web.login.loginCard.forgotPassword')}
        </TertiaryButton>
        <TertiaryButton
          onClick={onNewUserPress}
          style={{ width: '100%', marginTop: '16px', gap: 4 }}
        >
          <span style={{ color: colors.black }}>{t('web.login.loginCard.newToHevy')}</span>
          <span style={{ textDecoration: 'underline' }}>{t('web.login.loginCard.signUp')}</span>
        </TertiaryButton>
      </View>
    </View>
  );
};
